import React, { useState, useEffect, useCallback } from 'react'
import { Building2, RefreshCw, Download, ChevronDown, ChevronUp } from 'lucide-react'
import { AllCompaniesData, FunderFilter } from '../types'
import { fundersService } from '../services/fundersService'

interface AllCompanyAssociationsTabProps {
  filter?: FunderFilter
}

export const AllCompanyAssociationsTab: React.FC<AllCompanyAssociationsTabProps> = ({ filter }) => {
  const [companiesData, setCompaniesData] = useState<AllCompaniesData>({ companies: [], total_companies: 0, total_amount: 0, total_deals: 0, accounts: [] })
  const [loading, setLoading] = useState(true)
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set())
  const [exporting, setExporting] = useState(false)

  const loadCompanyData = useCallback(async () => {
    try {
      setLoading(true)
      const data = await fundersService.getAllCompanyAssociations(filter)
      setCompaniesData(data)
    } catch (error) {
      console.error('Error loading company data:', error)
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    loadCompanyData()
  }, [loadCompanyData])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  const toggleCompany = (companyKey: string) => {
    setExpandedCompanies(prev => {
      const newSet = new Set(prev)
      if (newSet.has(companyKey)) {
        newSet.delete(companyKey)
      } else {
        newSet.add(companyKey)
      }
      return newSet
    })
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      const csvContent = await fundersService.exportAllCompanies(filter)
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `all-companies-${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      console.error('Error exporting data:', error)
      alert('Failed to export data')
    } finally {
      setExporting(false)
    }
  }

  // Group companies by account
  const companiesByAccount = companiesData.companies.reduce((acc, company) => {
    if (!acc[company.account]) {
      acc[company.account] = []
    }
    acc[company.account].push(company)
    return acc
  }, {} as Record<string, typeof companiesData.companies>)

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        height: '200px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            animation: 'spin 1s linear infinite', 
            borderRadius: '50%', 
            height: '32px', 
            width: '32px', 
            border: '2px solid #3b82f6', 
            borderBottomColor: 'transparent',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
            Loading all company associations...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Building2 style={{ height: '20px', width: '20px', color: '#6b7280' }} />
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#111827',
              fontFamily: 'Lexend Deca, sans-serif',
              margin: 0
            }}>
              All Company Associations
            </h2>
            <span style={{ 
              fontSize: '14px', 
              color: '#6b7280',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              ({companiesData.total_companies} companies across {companiesData.accounts.length} accounts, {formatCurrency(companiesData.total_amount)} total)
            </span>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleExport}
              disabled={exporting || companiesData.companies.length === 0}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 16px',
                background: exporting ? '#f3f4f6' : '#10b981',
                color: exporting ? '#6b7280' : 'white',
                fontWeight: '500',
                borderRadius: '8px',
                border: 'none',
                cursor: exporting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!exporting && companiesData.companies.length > 0) {
                  e.currentTarget.style.background = '#059669'
                }
              }}
              onMouseLeave={(e) => {
                if (!exporting && companiesData.companies.length > 0) {
                  e.currentTarget.style.background = '#10b981'
                }
              }}
            >
              <Download style={{ height: '16px', width: '16px', marginRight: '8px' }} />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
            <button
              onClick={loadCompanyData}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 16px',
                background: 'white',
                color: '#374151',
                fontWeight: '500',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <RefreshCw style={{ height: '16px', width: '16px', marginRight: '8px' }} />
              Refresh
            </button>
          </div>
        </div>

        {/* Companies List Grouped by Account */}
        {companiesData.companies.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '64px',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
          }}>
            <Building2 style={{ 
              height: '48px', 
              width: '48px', 
              color: '#d1d5db', 
              margin: '0 auto 16px' 
            }} />
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '500', 
              color: '#6b7280',
              fontFamily: 'Lexend Deca, sans-serif',
              marginBottom: '8px'
            }}>
              No companies found
            </h3>
            <p style={{ 
              color: '#9ca3af',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              No companies found in the current dataset
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {Object.entries(companiesByAccount).map(([account, companies]) => {
              const accountTotal = companies.reduce((sum, c) => sum + c.total_amount, 0)
              const accountDeals = companies.reduce((sum, c) => sum + c.deal_count, 0)
              
              return (
                <div key={account} style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  overflow: 'hidden', 
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  {/* Account Header */}
                  <div style={{
                    padding: '20px',
                    background: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <h3 style={{ 
                        fontSize: '18px', 
                        fontWeight: '600', 
                        color: '#111827',
                        fontFamily: 'Lexend Deca, sans-serif',
                        margin: 0
                      }}>
                        {account}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ 
                            fontSize: '16px', 
                            fontWeight: '600', 
                            color: '#10b981',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            {formatCurrency(accountTotal)}
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#6b7280',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            Total Amount
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ 
                            fontSize: '16px', 
                            fontWeight: '600', 
                            color: '#3b82f6',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            {accountDeals}
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#6b7280',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            Total Deals
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ 
                            fontSize: '16px', 
                            fontWeight: '600', 
                            color: '#8b5cf6',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            {companies.length}
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#6b7280',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}>
                            Companies
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Companies within this account */}
                  <div style={{ padding: '0' }}>
                    {companies.map((company, index) => {
                      const companyKey = `${company.account}-${company.company_id}`
                      const isExpanded = expandedCompanies.has(companyKey)
                      
                      return (
                        <div key={companyKey}>
                          {/* Company Row */}
                          <div 
                            style={{
                              padding: '16px 20px',
                              borderBottom: index < companies.length - 1 || isExpanded ? '1px solid #f3f4f6' : 'none',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s'
                            }}
                            onClick={() => toggleCompany(companyKey)}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ 
                                    fontSize: '16px', 
                                    fontWeight: '500', 
                                    color: company.company_id === 'no-company-associations' ? '#dc2626' : '#111827',
                                    fontFamily: 'Lexend Deca, sans-serif',
                                    fontStyle: company.company_id === 'no-company-associations' ? 'italic' : 'normal'
                                  }}>
                                    {company.company_name}
                                  </span>
                                  {company.company_domain && (
                                    <span style={{ 
                                      fontSize: '14px', 
                                      color: '#6b7280',
                                      fontFamily: 'Lexend Deca, sans-serif'
                                    }}>
                                      {company.company_domain}
                                    </span>
                                  )}
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <span style={{ 
                                      fontSize: '12px', 
                                      color: '#6b7280',
                                      fontFamily: 'Lexend Deca, sans-serif'
                                    }}>
                                      {company.association_labels.join(', ')}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ 
                                    fontSize: '16px', 
                                    fontWeight: '600', 
                                    color: '#10b981',
                                    fontFamily: 'Lexend Deca, sans-serif'
                                  }}>
                                    {formatCurrency(company.total_amount)}
                                  </div>
                                  <div style={{ 
                                    fontSize: '12px', 
                                    color: '#6b7280',
                                    fontFamily: 'Lexend Deca, sans-serif'
                                  }}>
                                    Total Amount
                                  </div>
                                </div>
                                <div style={{ textAlign: 'right', minWidth: '80px' }}>
                                  <div style={{ 
                                    fontSize: '16px', 
                                    fontWeight: '600', 
                                    color: '#3b82f6',
                                    fontFamily: 'Lexend Deca, sans-serif'
                                  }}>
                                    {company.deal_count}
                                  </div>
                                  <div style={{ 
                                    fontSize: '12px', 
                                    color: '#6b7280',
                                    fontFamily: 'Lexend Deca, sans-serif'
                                  }}>
                                    {company.deal_count === 1 ? 'Deal' : 'Deals'}
                                  </div>
                                </div>
                                <div style={{ minWidth: '24px', display: 'flex', justifyContent: 'center' }}>
                                  {isExpanded ? (
                                    <ChevronUp style={{ height: '16px', width: '16px', color: '#6b7280' }} />
                                  ) : (
                                    <ChevronDown style={{ height: '16px', width: '16px', color: '#6b7280' }} />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Expanded Deal Details */}
                          {isExpanded && (
                            <div style={{
                              padding: '0 20px 16px 20px',
                              background: '#f9fafb',
                              borderBottom: index < companies.length - 1 ? '1px solid #e5e7eb' : 'none'
                            }}>
                              <div style={{ 
                                display: 'grid',
                                gap: '8px',
                                maxHeight: '300px',
                                overflowY: 'auto'
                              }}>
                                {company.deals
                                  .sort((a, b) => new Date(b.closed_won_date).getTime() - new Date(a.closed_won_date).getTime())
                                  .map((deal, dealIndex) => (
                                  <div 
                                    key={`${deal.deal_id}-${dealIndex}`}
                                    style={{
                                      padding: '12px 16px',
                                      background: 'white',
                                      borderRadius: '8px',
                                      border: '1px solid #e5e7eb'
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                          <span style={{ 
                                            fontSize: '14px', 
                                            color: '#6b7280',
                                            fontFamily: 'Lexend Deca, sans-serif',
                                            minWidth: '80px'
                                          }}>
                                            {formatDate(deal.closed_won_date)}
                                          </span>
                                          <span style={{ 
                                            fontSize: '14px', 
                                            fontWeight: '500', 
                                            color: '#111827',
                                            fontFamily: 'Lexend Deca, sans-serif',
                                            flex: 1
                                          }}>
                                            {deal.deal_name}
                                          </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
                                          <span style={{ 
                                            fontSize: '12px', 
                                            color: '#6b7280',
                                            fontFamily: 'Lexend Deca, sans-serif',
                                            minWidth: '80px'
                                          }}>
                                            {/* Empty space for alignment */}
                                          </span>
                                          <span style={{ 
                                            fontSize: '12px',
                                            padding: '2px 8px',
                                            background: '#f3f4f6',
                                            color: '#6b7280',
                                            borderRadius: '12px',
                                            fontFamily: 'Lexend Deca, sans-serif'
                                          }}>
                                            {deal.association_label}
                                          </span>
                                        </div>
                                      </div>
                                      <span style={{ 
                                        fontSize: '14px', 
                                        fontWeight: '600', 
                                        color: '#10b981',
                                        fontFamily: 'Lexend Deca, sans-serif',
                                        minWidth: '100px',
                                        textAlign: 'right'
                                      }}>
                                        {formatCurrency(deal.deal_amount)}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}